import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@sandtech-hub-monorepo/ui-kit';

import { EnrollConsentNotice } from '../../../components/program/EnrollConsentNotice';
import {
  CatalogueProgram,
  ProgramCta,
  programCta,
  ProgramType,
  usePrograms,
} from '../../../data/programs';
import { AppRoutesPaths, subscribePath } from '../../../routes/types';
import { CatalogueFilterBar, CatalogueView } from './CatalogueFilterBar';
import { ProgramCard } from './ProgramCard';
import { SectionHeading } from './SectionHeading';

interface Props {
  type: ProgramType;
  /** Wrapper classes — the standalone page adds max-width + padding; the
   *  Learning "All Programs" tab embeds it inside its own container. */
  className?: string;
}

/**
 * The catalogue content (filter bar + the Available grid) without the page
 * hero. Rendered by the standalone catalogue page and reused inside the
 * Learning "All Programs" tab.
 */
export const ProgramsCatalogueBody = ({ type, className }: Props) => {
  const navigate = useNavigate();
  const { available, loading } = usePrograms(type);

  const [view, setView] = useState<CatalogueView>('grid');

  const total = available.length;

  // Shown once for the whole grid rather than per card: only relevant while
  // at least one program's CTA can actually lead towards enrolling
  // (`start`) — `continue`, `completed` and `unavailable` never do.
  const canStartAny = available.some(
    (program) => programCta(program).intent === 'start'
  );

  const gridClass =
    view === 'grid'
      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch'
      : 'grid grid-cols-1 gap-6 items-stretch';

  // The abbreviation is the `:programId` key, same as the shared ui-kit-site
  // cards use, so /programs and /program agree on one key.
  const goToDetail = (program: CatalogueProgram) =>
    navigate(
      AppRoutesPaths.Program.replace(':programId', program.abbreviation)
    );

  /*
   * Starting a program means choosing how to start it, so this opens the plan
   * picker scoped to the program rather than enrolling. Enrolment happens at
   * the end of that choice — on "Stay on Free", or once a checkout is
   * confirmed — never on this click.
   */
  const goToPlans = (program: CatalogueProgram) =>
    navigate(subscribePath(program.id));

  /*
   * Where the card's primary button goes, by what it offered to do. Only the
   * two live intents are listed: `completed` and `unavailable` render a
   * disabled button, so nothing dispatches here for them.
   *
   * `continue` goes to /learning rather than to the program page, matching
   * what the detail page's own enrolled CTA does. The card already carries a
   * separate "View details" link for the program itself.
   */
  const runCta = (program: CatalogueProgram, cta: ProgramCta) => {
    if (cta.intent === 'continue') {
      navigate(AppRoutesPaths.Learning);
      return;
    }

    if (cta.intent === 'start') {
      goToPlans(program);
    }
  };

  return (
    <div className={cn('flex w-full flex-col gap-10', className)}>
      {available.length > 0 && (
        <section className="flex flex-col gap-5">
          <SectionHeading
            title="Available Programs"
            count={available.length}
            subtitle="Start with our first cohort of programs. Choose the path that fits your goals."
            actions={
              <CatalogueFilterBar
                count={total}
                view={view}
                onViewChange={setView}
              />
            }
          />
          {canStartAny && <EnrollConsentNotice ctaLabel="Start Now" />}

          <div className={gridClass}>
            {available.map((program) => {
              // Read once per card: the same decision names the button and
              // picks where it goes.
              const cta = programCta(program);

              return (
                <ProgramCard
                  key={program.id}
                  program={program}
                  layout={view}
                  cta={cta}
                  onViewDetails={() => goToDetail(program)}
                  onPrimaryAction={() => runCta(program, cta)}
                />
              );
            })}
          </div>
        </section>
      )}

      {loading && (
        <p className="py-16 text-center text-muted-foreground">
          Loading programs…
        </p>
      )}

      {!loading && total === 0 && (
        <p className="py-16 text-center text-muted-foreground">
          No programs available yet.
        </p>
      )}
    </div>
  );
};
